"use client";

import { useMemo } from "react";
import type { Id } from "@convex/_generated/dataModel";
import {
  DEFAULT_CHECKOUT_TRUCK_CAPACITY,
  type ResolvedTruckCapacity,
} from "@shared/products/truck-capacity";
import { useListingShipmentSeed } from "@/hooks/checkout/use-listing-shipment-seed";
import {
  buildListingShipments,
  useListingBuyNow,
} from "@/hooks/checkout/use-listing-buy-now";
import { useCartFreightQuote } from "@/hooks/checkout/use-cart-freight-quote";
import { checkoutQuoteSummary } from "@shared/orders/checkout-quote-summary";
import {
  termsLoadsFor,
  useTermsPayment,
} from "@/hooks/checkout/use-terms-payment";
import { useListingActionSettled } from "./listing-action-resolution";
import type { useListingBuyState } from "./listing-buy-state";

export interface ListingProduct {
  productId: Id<"products">;
  productPublicIdentifier?: string;
  priceCents: number;
  productTitle: string;
  productTypeSlug: string;
  productSlug?: string;
  truckCapacity?: number;
  truckOptions?: ResolvedTruckCapacity[];
}

export type ListingBuyState = ReturnType<typeof useListingBuyState>;

export function isResolvingDelivery(state: ListingBuyState) {
  return (
    state.quantityPending ||
    state.delivery.lookingUp ||
    state.delivery.addressPersistPending ||
    state.delivery.hasUnresolvedInput
  );
}

function useListingShipments(product: ListingProduct, state: ListingBuyState) {
  const { quantity, deliveryWindow, delivery } = state;
  const shipmentProduct = useMemo(
    () => ({
      productId: product.productId,
      quantity,
      truckCapacity: product.truckCapacity ?? DEFAULT_CHECKOUT_TRUCK_CAPACITY,
      truckOptions: product.truckOptions,
      minQuantity: state.checkoutMinimum,
      leadTimeDays: state.leadTimeDays,
    }),
    [
      product.productId,
      quantity,
      product.truckCapacity,
      product.truckOptions,
      state.checkoutMinimum,
      state.leadTimeDays,
    ],
  );
  const location = delivery.location;
  const shipments = useMemo(
    () =>
      location?.line1
        ? buildListingShipments({
            productId: product.productId,
            shipmentProduct,
            deliveryWindow,
            hasDockOnSite: state.hasDockOnSite,
            location,
          })
        : [],
    [
      product.productId,
      shipmentProduct,
      deliveryWindow,
      state.hasDockOnSite,
      location,
    ],
  );
  return { shipmentProduct, shipments, location };
}

function useListingQuote(
  product: ListingProduct,
  state: ListingBuyState,
  shipments: ReturnType<typeof useListingShipments>["shipments"],
) {
  const { quantity } = state;
  const quoteItems = useMemo(
    () => [{ productId: product.productId, quantity }],
    [product.productId, quantity],
  );
  const isSettled = useListingActionSettled();
  const freight = useCartFreightQuote(
    quoteItems,
    shipments,
    isSettled && !isResolvingDelivery(state),
    "listing",
  );
  const summary = checkoutQuoteSummary(product.priceCents * quantity, freight.quote);
  const quoted = freight.quote !== null && !freight.loading;
  const terms = useTermsPayment(
    quoted
      ? termsLoadsFor(shipments, freight.quote?.shipmentQuotes)
      : undefined,
  );
  return {
    freight,
    goodsCents: summary.goodsCents,
    totalCents: summary.totalBeforeTaxCents ?? summary.goodsCents,
    terms,
  };
}

export function useListingPurchase(
  product: ListingProduct,
  state: ListingBuyState,
) {
  const { quantity, deliveryWindow } = state;
  const { shipmentProduct, shipments, location } = useListingShipments(
    product,
    state,
  );
  const seedShipment = useListingShipmentSeed(
    shipmentProduct,
    deliveryWindow,
    location,
    state.hasDockOnSite,
  );
  const { freight, goodsCents, totalCents, terms } = useListingQuote(
    product,
    state,
    shipments,
  );
  const purchase = useListingBuyNow({
    productId: product.productId,
    quantity,
    unitPriceCents: product.priceCents,
    snapshot: { ...product, unitPriceCents: product.priceCents },
    shipmentProduct,
    location,
    deliveryWindow,
    hasDockOnSite: state.hasDockOnSite,
    seedShipment,
    shipments,
    ...useExpectedCosts(freight),
    onPriceDrift: freight.refresh,
    terms,
  });
  return {
    ...purchase,
    seedShipment,
    terms,
    freight,
    goodsCents,
    totalCents,
    loadCount: shipments.length,
    complete: shipments.length > 0,
  };
}

function useExpectedCosts(freight: ReturnType<typeof useCartFreightQuote>) {
  return {
    expectedFreightCents: freight.quote?.freightCents,
    expectedProductSubtotals: freight.quote?.productSubtotals,
  };
}

export type ListingPurchase = ReturnType<typeof useListingPurchase>;
